import type { LoadContext } from "@/core/features/index.ts";
import { fetchModule } from "@/core/storage/moduleCache.ts";
import wasmUrl from "./pkg/imageops/imageops_bg.wasm?url";

/** Hashed by the bundler, so a new build never serves a stale module from the cache. */
export const IMAGEOPS_WASM_URL = wasmUrl;

export type ResampleQuality = 0 | 1 | 2;

export const RESAMPLE_PIXELATED: ResampleQuality = 0;
export const RESAMPLE_FAST: ResampleQuality = 1;
export const RESAMPLE_BEST: ResampleQuality = 2;

/**
 * wasm-bindgen types its buffers as possibly shared, because a threaded module's memory is
 * a SharedArrayBuffer. This module is built single-threaded, so the bytes are always backed
 * by a plain ArrayBuffer and can be handed straight to Blob and ImageData.
 */
type OwnedBytes = Uint8Array<ArrayBuffer>;

const asOwned = (bytes: Uint8Array): OwnedBytes => bytes as OwnedBytes;

export type EncodedImage = {
  readonly bytes: OwnedBytes;
  readonly quality: number;
  readonly sizeBytes: number;
};

export type ImageOpsModule = {
  resizeRgba(
    rgba: OwnedBytes,
    widthPx: number,
    heightPx: number,
    targetWidthPx: number,
    targetHeightPx: number,
    quality: ResampleQuality,
  ): OwnedBytes;
  encodeRgba(
    rgba: OwnedBytes,
    widthPx: number,
    heightPx: number,
    format: "png" | "jpeg",
    quality: number,
  ): EncodedImage;
  encodeRgbaWithin(
    rgba: OwnedBytes,
    widthPx: number,
    heightPx: number,
    format: "png" | "jpeg",
    maxBytes: number,
  ): EncodedImage;
};

/**
 * The glue JS is a small lazy chunk; the .wasm goes through the module cache so the
 * download reports progress and survives offline.
 */
export async function loadImageOps(context: LoadContext): Promise<ImageOpsModule> {
  const [module, response] = await Promise.all([
    import("./pkg/imageops/imageops.js"),
    fetchModule(wasmUrl, { signal: context.signal, onProgress: context.report }),
  ]);
  await module.default({ module_or_path: response });
  return {
    resizeRgba: (...args) => asOwned(module.resizeRgba(...args)),
    encodeRgba: (rgba, width, height, format, quality) => {
      const encoded = module.encodeRgba(rgba, width, height, format, quality);
      return {
        bytes: asOwned(encoded.bytes),
        quality: encoded.quality,
        sizeBytes: encoded.sizeBytes,
      };
    },
    encodeRgbaWithin: (rgba, width, height, format, maxBytes) => {
      const encoded = module.encodeRgbaWithin(rgba, width, height, format, maxBytes);
      return {
        bytes: asOwned(encoded.bytes),
        quality: encoded.quality,
        sizeBytes: encoded.sizeBytes,
      };
    },
  };
}
